import anime from 'animejs';
import { schedule } from '../data/schedule';
import { getWaitTime } from '../wait-time';
import { fitText } from './fit-text';
import { fizzle } from './fizzle';
import { stillAlive } from '../easter-eggs/still-alive';

/**
 * The break screen's schedule block: "A SEGUIRE" plus the five runs after it.
 *
 * When the schedule runs out a row renders `> GIOCATORE n PREMI START <` instead, whose
 * words the fizzle scrambler flickers; `n` counts filler rows, not schedule positions.
 */
export const breakScheduleRenderer = {
    breakNextGameNameAlternateAnimation: undefined,
    breakNextGameCategoryAlternateAnimation: undefined,
    endCounter: 1,
    getRunnerFormattedString: (run) => {
        const runners = run?.submission?.runners?.map(r => r.user?.name) ?? [];
        return runners.join(' <span class="no-hl">e</span> ');
    },

    getRunElapsedFormattedString: (run) => {
        let estimateDuration = run?.duration ?? 0;
        let estimate = schedule.getDurationFromSeconds(estimateDuration);
        return estimate.format('HH:mm:ss');
    },

    updateAndShowAllBreakRuns: () => {
        // The schedule redraws on every schedule change…
        let timeline = anime.timeline();
        breakScheduleRenderer.updateBreakNextRun(timeline);
        for (let x = 1; x <= 5; x++) {
            breakScheduleRenderer.updateBreakNextNthRun(x, timeline);
        }

        // The ad/incentive carousel is driven by breakCarousel (js/break-carousel.js),
        // which init wires to the same schedule refresh and starts exactly once.
    },

    updateBreakRunCommonData: (rootElement, data, index) => {
        const nameElement = document.querySelector(rootElement + ' .what .name');
        const categoryElement = document.querySelector(rootElement + ' .what .category');
        const runnerElement = document.querySelector(rootElement + ' .who .name');
        const elapsedElement = document.querySelector(rootElement + ' .who .elapsed');

        let changed = false;
        changed = nameElement.innerHTML === data?.name ?? '';
        changed = categoryElement.innerHTML === data?.submission?.category ?? '';

        const runnerFormattedString = breakScheduleRenderer.getRunnerFormattedString(data);

        const gameName = data?.name ?? '';
        const categoryName = data?.submission?.category ?? '';
        nameElement.innerHTML = gameName;
        categoryElement.innerHTML = categoryName;
        runnerElement.innerHTML = runnerFormattedString;
        elapsedElement.innerHTML = breakScheduleRenderer.getRunElapsedFormattedString(data);

        const whoElement = document.querySelector(rootElement + ' .who');
        if (runnerFormattedString === '') {
            whoElement.classList.add('hide');
        } else {
            whoElement.classList.remove('hide');
        }

        if (gameName === '' && categoryName === '') {
            const chevronElement = document.querySelector(rootElement + ' .chevron');
            chevronElement.classList.add('hide');
            const endElement = document.querySelector(rootElement + ' .end');
            endElement.classList.remove('hide');
            const endElementSpan = document.querySelector(rootElement + ' .end span');
            endElementSpan.innerHTML = "> <span class='fizzle fizzle-1 row-" + breakScheduleRenderer.endCounter + "'>GIOCATORE</span> <span class='fizzle fizzle-2 row-" + breakScheduleRenderer.endCounter + "'>" + breakScheduleRenderer.endCounter + "</span> <span class='fizzle fizzle-3 row-" + breakScheduleRenderer.endCounter + "'>PREMI</span> <span class='fizzle fizzle-4 row-" + breakScheduleRenderer.endCounter + "'>START</span> <";
            breakScheduleRenderer.endCounter++;
        }

        if (index === 0) {
            nameElement.classList.remove('alternate');
            categoryElement.classList.remove('alternate');

            if (nameElement.getBoundingClientRect().top !== categoryElement.getBoundingClientRect().top) {
                nameElement.classList.add('alternate');
                categoryElement.classList.add('alternate');
            }
        }

        fitText.decreaseFontSizeUntilItFits(nameElement, 2);

        return changed;
    },

    startFizzleWatcher: () => {
        const fizzleableLength = fizzle.getFizzleable().length / 4;
        console.log("Started fizzle watcher: length", fizzleableLength);
        if (fizzleableLength <= 0) return;

        // Exactly six filler rows is the cue the Portal easter egg listens for. It is
        // dormant, so in practice this always falls through to the scrambler.
        if (fizzleableLength === 6) {
            stillAlive.startSong();
            return;
        }

        const fizzleInterval = 720000 / (fizzleableLength + 1) / 2;
        console.log("Fizzling every", fizzleInterval);
        fizzle.fizzleWatcherInterval = setInterval(function () {
            fizzle.fizzleTimer(4, 25);
            console.log("Fizzled");
        }, fizzleInterval);
    },

    updateBreakNextRun: (timeline) => {
        const nextRun = schedule.getNextRun(0);
        const rootElement = '#break-next .next-run';

        if (nextRun.length === 0)
            return;

        breakScheduleRenderer.updateBreakRunCommonData(rootElement, nextRun, 0);
        breakScheduleRenderer.showBreakRun(rootElement, timeline, 0);

        console.log("NEXT RUN:", nextRun);
    },

    updateBreakNextNthRun: (index, timeline) => {
        const nextRun = schedule.getNextRun(index);
        const rootElement = '#break-next .future-runs .game:nth-child(' + index + ')';

        breakScheduleRenderer.updateBreakRunCommonData(rootElement, nextRun, index);

        const timeInElement = document.querySelector(rootElement + ' .when .in');
        const timeEstimateElement = document.querySelector(rootElement + ' .when .estimate');

        // .when .in/.estimate
        const waitTime = getWaitTime(schedule.getRunStartDate(nextRun));
        timeInElement.innerHTML = waitTime.label;
        timeEstimateElement.innerHTML = waitTime.duration;

        breakScheduleRenderer.showBreakRun(rootElement, timeline, index);
    },

    showBreakRun: (rootElement, timeline, index) => {
        const animeData = {
            targets: rootElement,
            opacity: 1,
            easing: 'easeInOutQuad',
            duration: 2000,
            delay: index === 0 ? 1500 : 0,
        };
        if (!!timeline) {
            timeline.add(animeData, '-=1000');
        } else {
            anime(animeData);
        }

        if (index === 0) {
            breakScheduleRenderer.breakNextGameNameAlternateAnimation = anime({
                // hides name if alternate mode is on
                targets: document.querySelector(rootElement + ' .name.alternate'),
                opacity: 0,
                easing: 'linear',
                duration: 250,
                direction: 'alternate',
                loop: true,
                delay: 4000,
                endDelay: 3000,
            });
            breakScheduleRenderer.breakNextGameCategoryAlternateAnimation = anime({
                // shows category if alternate mode is on
                targets: document.querySelector(rootElement + ' .category.alternate'),
                opacity: 1,
                easing: 'linear',
                duration: 250,
                direction: 'alternate',
                loop: true,
                delay: 4000,
                endDelay: 3000,
            });
        }
    },

};

window.breakScheduleRenderer = breakScheduleRenderer;
